/**
 * Custom React hook to load, initialize, and configure a Marketo form dynamically.
 *
 * Responsibilities:
 * - Inject the Marketo forms2.js script (if not already loaded)
 * - Load the specified form via `MktoForms2.loadForm`
 * - Attach validation, success, and styling logic to the form
 * - Optionally clean Marketo's default styles
 * - Prefill fields from URL query parameters
 * - Reorder form layout for special cases (e.g. newsletter form)
 *
 * @param {object} options
 * @param {string} options.formId - Marketo form ID
 * @param {string} options.baseUrl - Base URL for the Marketo instance
 * @param {string} options.munchkinId - Munchkin account ID
 * @param {string} options.tyPage - Thank You page URL for redirection on success
 * @param {boolean} options.cleanStyles - Whether to remove Marketo default styles
 * @returns {{ elementId: string }} - The ID of the DOM element to render the form into
 */

import { useEffect, useMemo } from 'react';
import cleanMktoStyles from '../../MarketoForm/cleanMktoStyles';

// Control flags to avoid re-injecting the script or re-initializing
let isMarketoScriptAppended = false;
let isMarketoInitialized = false;

function useMarketoForm({ formId, baseUrl, munchkinId, tyPage, cleanStyles }) {
  // Generate a unique element ID for the form wrapper, memoized for performance
  const elementId = useMemo(() => `mktoFormWrapper_${formId}`, [formId]);

  // Construct the full script source URL
  const scriptSrc = `${baseUrl}/js/forms2/js/forms2.min.js`;

  useEffect(() => {
    // Skip if required values are missing
    if (!formId || !baseUrl || !munchkinId) return;

    /**
     * Inject the Marketo forms2.js script if not already present,
     * then initialize the form.
     */
    const loadMarketoScript = () => {
      if (window.MktoForms2) {
        initMarketo();
        return;
      }

      if (!isMarketoScriptAppended) {
        const script = document.createElement('script');
        script.src = scriptSrc;
        script.async = true;
        script.onload = () => {
          if (window.MktoForms2) initMarketo();
        };
        document.body.appendChild(script);
        isMarketoScriptAppended = true;
      }
    };

    /**
     * Initializes the Marketo form, configures behavior, styling, hooks, and redirection
     */
    const initMarketo = () => {
      if (isMarketoInitialized || !window.MktoForms2) return;
      isMarketoInitialized = true;

      // Required to resolve CORS issues with iframe posting
      window.MktoForms2.setOptions({
        formXDPath: `/rs/${munchkinId}/images/marketo-xdframe-relative.html`,
      });

      // Load and render the form
      window.MktoForms2.loadForm(baseUrl, munchkinId, formId, (form) => {
        if (cleanStyles) cleanMktoStyles(elementId);
        handleValidationHooks(form);
        prefillFieldsFromQuery(form);
        observeMktoErrors();

        // Track analytics on form submit instead of onSuccess
        form.onSubmit(() => {
          if (window._satellite) {
            var form_vals = form.vals();

            window._satellite.track('mrktoFormSubmit', {
              marketo_form_email: form_vals['Email'],
              form_values: form_vals,
            });
          }
        });

        // Redirect to thank-you page on successful form submission
        form.onSuccess((values, followUpUrl) => {
          window.location.href = tyPage;
          return false; // Prevent default redirect behavior
        });
      });

      // Clean styles again after the form has rendered
      window.MktoForms2.onFormRender(() => {
        if (cleanStyles) cleanMktoStyles(elementId);
      });

      /**
       * Special layout adjustment for newsletter form:
       * Moves the button row up to the first section.
       * (Hacky, marked as TODO)
       */
      window.MktoForms2.whenReady(function (form) {
        const firstFirst = document.querySelector(
          '.c-advance-marketo-form .mktoForm :nth-child(2)'
        );
        const two = document.querySelector('.c-advance-marketo-form .mktoForm .mktoButtonRow');
        if (firstFirst && two) {
          const wrapperDiv = document.createElement('div');
          wrapperDiv.classList.add('wrapper');
          wrapperDiv.appendChild(firstFirst);
          wrapperDiv.appendChild(two);
          const container = document.querySelector('.c-advance-marketo-form .mktoForm');
          container.insertBefore(wrapperDiv, container.firstChild);
        }
      });
    };

    /**
     * Prefills form fields using query string parameters in the URL.
     */
    const prefillFieldsFromQuery = (form) => {
      const params = new URLSearchParams(window.location.search);
      const formEl = form.getFormElem()[0];

      for (const [key, value] of params.entries()) {
        const field = formEl.elements[key];
        if (field) field.value = decodeURIComponent(value);
      }
    };

    /**
     * Observes mutation changes in the form DOM and removes inline error styles.
     * Prevents Mkto from forcibly styling error blocks.
     */
    const observeMktoErrors = () => {
      const formEl = document.querySelector(`#${elementId} form`);
      if (!formEl) return;

      const observer = new MutationObserver((mutations) => {
        mutations.forEach(({ addedNodes }) => {
          addedNodes.forEach((node) => {
            if (node.classList?.contains('mktoError') && node.hasAttribute('style')) {
              node.removeAttribute('style');
            }
          });
        });
      });

      observer.observe(formEl, { childList: true, subtree: true });
    };

    /**
     * Registers a validation hook.
     * Currently a placeholder for future checks like domain validation, reCAPTCHA, etc.
     */
    const handleValidationHooks = (form) => {
      form.onValidate((isValid) => {
        if (!isValid) return;
        // TODO: Add custom validation logic (e.g., reCAPTCHA, email domain check)
      });
    };

    // Start loading the script and form
    loadMarketoScript();
  }, [formId, baseUrl, munchkinId, tyPage, elementId]);

  // Return the DOM element ID so the parent can render the form container
  return { elementId };
}

export default useMarketoForm;
